import { z } from "zod";
import { UserAccountType } from "../accounts";
import { bankAccountSchema } from "../bank-accounts/schemas/bankAccount.schema";
import { schemes } from "../schema-validation";
import {
  EWorkerInformationSection,
  type TWorkerInformationSection,
} from "./EWorkerInformationSection";
import { ESSNVerificationStatus } from "../kyc";
import { EPaycardSSNVerificationDeclinedReasonCodes } from "../pay-cards";

export type TWorkerShareableLinkSectionTitleForEmail =
  | "additional information required"
  | "account login information"
  | "bank information"
  | "employee survey"
  | "paperwork"
  | "paycard enrollment"
  | "profile information"
  | "instant pay enrollment"
  | "transaction retry";

export const workerShareableLinkSectionToEmailTitleMap: Record<
  TWorkerInformationSection,
  TWorkerShareableLinkSectionTitleForEmail
> = {
  [EWorkerInformationSection.ACCOUNT_INFO]: "account login information",
  [EWorkerInformationSection.SURVEY]: "employee survey",
  [EWorkerInformationSection.BANK_INFO]: "bank information",
  [EWorkerInformationSection.CUSTOM_PAPERWORK]: "paperwork",
  [EWorkerInformationSection.PAYCARD]: "paycard enrollment",
  [EWorkerInformationSection.PAYCARD_SSN_ID_VERIFICATION]:
    "additional information required",
  [EWorkerInformationSection.PAYCARD_SSN_SSC_VERIFICATION]:
    "additional information required",
  [EWorkerInformationSection.PROFILE_INFO]: "profile information",
  [EWorkerInformationSection.INSTANT_PAY]: "instant pay enrollment",
  [EWorkerInformationSection.TRANSACTION]: "transaction retry",
};

export const workerInformationSectionSchema = z.object({
  name: z.nativeEnum(EWorkerInformationSection),
});

export const workerShareableLinksTokenPayloadSchema = z.object({
  companyID: schemes.companyID(),
  connectionID: z.string().optional(),
  email: schemes.email(),
  formTemplateIDs: z.array(z.string()).optional(),
  isShareableLink: z.literal(true),
  sections: z.array(workerInformationSectionSchema),
  testMode: z.boolean(),
  transactionID: z.string().optional(),
  whiteLabelID: z.string(),
  workerID: schemes.mongoObjectId(),
  workerType: z.enum([
    UserAccountType.CompanyWorker,
    UserAccountType.CompanyContractor,
  ]),
});

export const paycardSSNVerificationSchema = z.object({
  declined_reason_codes: z.array(
    z.nativeEnum(EPaycardSSNVerificationDeclinedReasonCodes)
  ),
  declined_reason_description: z.string().max(500).optional(),
});

export const workerInformationCompanySchema = z.object({
  company_id: schemes.companyID(),
  business_name: z.string(),
});

export const workerInformationFormsSchema = z.object({
  formID: z.string(),
  formTemplateID: z.string().optional(),
  label: z.string(),
});

export const workerInformationWorkerSchema = z.object({
  _id: schemes.mongoObjectId(),
  address: z.string().optional(),
  address_line2: z.string().nullish(),
  city: z.string().optional(),
  first_name: z.string().optional(),
  hash: z.string().optional(),
  last_name: z.string().optional(),
  middle_name: z.string().optional(),
  dob: z.string().optional(),
  phone_number: z.string().optional(),
  ssn: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  paycard_enabled: z.boolean().optional(),
  paycard_requested: z.boolean().optional(),
  ssn_verification_status: z.nativeEnum(ESSNVerificationStatus).optional(),
  user_type: z.nativeEnum(UserAccountType).optional(),
  instant_pay_enabled: z.boolean().optional(),
});

export const workerInformationPayloadSchema = z.object({
  company: workerInformationCompanySchema,
  bankAccounts: z.array(bankAccountSchema).nullish(),
  forms: z.array(workerInformationFormsSchema).nullish(),
  logoURL: z.string().nullish(),
  paycardSSNVerification: paycardSSNVerificationSchema.nullish(),
  worker: workerInformationWorkerSchema,
  tokenPayload: workerShareableLinksTokenPayloadSchema,
});

export type TWorkerShareableLinksSection = z.infer<
  typeof workerInformationSectionSchema
>;
export type TWorkerShareableLinksTokenPayload = z.infer<
  typeof workerShareableLinksTokenPayloadSchema
>;
export type TWorkerInformationPayload = z.infer<
  typeof workerInformationPayloadSchema
>;
